// Administración de usuarios del ERP (solo admins). Toda la operación real
// ocurre en la Edge Function "admin-usuarios" (servidor de Supabase, con la
// service_role key): aquí solo se pinta la UI y se invoca la function con el
// JWT del admin logueado vía supa.functions.invoke.
//
// Incluye además la administración de ROLES PERSONALIZADOS: se guardan en
// tenant_config (clave erp:roles_personalizados) y definen a qué módulos y
// submódulos tiene acceso cada rol mediante interruptores por página.
import { rolExigeMFA } from "../auth/session.js";
import { getRolesPersonalizados, getRolesSistemaOverrides, saveRolesPersonalizados, saveRolesSistemaOverrides } from "../core/cloudSync.js";
import { C, NAV_GRUPOS, ROLE_PAGES, ROLES_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { supa } from "../core/supabaseClient.js";
import { registrarEvento } from "../core/auditLog.js";
import { detalleSeguro } from "../core/bitacora.js";
import { btnStyle, escapeHTML, fmtDate, genId, inputStyle, traducirErrorAuth } from "../core/utils.js";
import { render } from "../ui/app.js";
import { getPoliticaSesion } from "../ui/inactividad.js";

const NAVY = C.mathiz;

// Páginas asignables que no aparecen en el menú lateral (se llega por banners).
const PAGINAS_EXTRA_ROLES = [
  { id: "alertas", label: "Centro de Alertas", icon: "🔔" }
];

async function llamarAdminUsuarios(payload) {
  const { data, error } = await supa.functions.invoke("admin-usuarios", { body: payload });
  if (error) {
    // FunctionsHttpError trae la respuesta con el mensaje real del servidor
    let msg = error.message || "Error de conexión con el servidor";
    try {
      const ctx = await error.context?.json?.();
      if (ctx?.error) msg = ctx.error;
    } catch { /* sin cuerpo JSON */ }
    throw new Error(traducirErrorAuth(msg));
  }
  if (data?.error) throw new Error(traducirErrorAuth(data.error));
  auditarAdminUsuario(payload, data);
  return data;
}

// Lo que un administrador le hace a una cuenta queda en la bitácora del
// usuario: quién, a quién y qué (nunca la contraseña).
function auditarAdminUsuario(payload, data) {
  if (!payload || !payload.accion || payload.accion === "listar") return;
  const id = payload.userId || (data && (data.id || (data.user && data.user.id))) || payload.email || "—";
  const u = (state.usuariosLista || []).find(x => x.id === payload.userId) || {};
  const detalle = detalleSeguro({ ...payload, userId: undefined });
  const limpio = detalle ? Object.fromEntries(Object.entries(detalle).filter(([, v]) => v !== undefined)) : null;
  registrarEvento("usuarios", id, "usuario_" + payload.accion, {
    referencia: payload.email || u.email || null,
    detalle: { ...(limpio || {}), ...(u.rol && payload.accion === "cambiar_rol" ? { rol_anterior: u.rol } : {}) }
  });
}

async function recargarUsuarios() {
  state.usuariosCargando = true;
  state.usuariosError = null;
  render();
  try {
    const data = await llamarAdminUsuarios({ accion: "listar" });
    state.usuariosLista = data.usuarios || [];
  } catch (e) {
    state.usuariosError = e.message;
  }
  state.usuariosCargando = false;
  render();
}

// Información de despliegue de un rol (sistema o personalizado)
function infoRol(rol) {
  if (ROLES_INFO[rol]) return ROLES_INFO[rol];
  const rp = getRolesPersonalizados().find(r => r.id === rol);
  if (rp) return { label: rp.nombre, icon: "🎭" };
  return { label: rol, icon: "❓" };
}

function opcionesRolesHTML(rolSeleccionado) {
  const sistema = Object.entries(ROLES_INFO)
    .map(([r, info]) => `<option value="${r}" ${rolSeleccionado === r ? "selected" : ""}>${info.icon} ${info.label}</option>`).join("");
  const personalizados = getRolesPersonalizados()
    .map(rp => `<option value="${escapeHTML(rp.id)}" ${rolSeleccionado === rp.id ? "selected" : ""}>🎭 ${escapeHTML(rp.nombre)}</option>`).join("");
  return sistema + (personalizados ? `<optgroup label="── Roles personalizados ──">${personalizados}</optgroup>` : "");
}

export function renderUsuarios() {
  // Primera visita: dispara la carga (el flag evita relanzarla en cada render)
  if (state.usuariosLista === undefined && !state.usuariosCargando && !state.usuariosError) {
    state.usuariosLista = undefined;
    recargarUsuarios();
  }
  if (!state.usuariosTab) state.usuariosTab = "usuarios";
  const tab = state.usuariosTab;

  const header = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;padding-bottom:10px;border-bottom:3px solid ${C.gold};flex-wrap:wrap;gap:10px;">
      <div style="display:flex;gap:10px;align-items:center;">
        <img src="${window.LOGO}" alt="Aura" style="height:42px;object-fit:contain;"/>
        <div>
          <h2 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">👥 Usuarios</h2>
          <p style="margin:3px 0 0;color:${C.muted};font-size:11.5px;">Crea, invita y administra los usuarios y los roles de acceso — sin salir del ERP</p>
        </div>
      </div>
      <div style="display:flex;gap:10px;">
        ${tab === "usuarios" ? `
          <button data-action="usuarios-recargar" style="${btnStyle(null, "secondary")}">↺ Actualizar</button>
          <button data-action="usuarios-nuevo" style="${btnStyle("#6b7280")}">+ Nuevo usuario</button>
        ` : `
          <button data-action="roles-nuevo" style="${btnStyle(NAVY)}">🎭 + Crear rol</button>
        `}
      </div>
    </div>
  `;

  const tabBtn = (id, label) => `
    <button data-action="usuarios-tab" data-tab="${id}" style="padding:9px 18px;border:none;border-bottom:3px solid ${tab === id ? NAVY : "transparent"};background:transparent;font-weight:${tab === id ? 800 : 600};font-size:12px;color:${tab === id ? NAVY : C.muted};cursor:pointer;font-family:inherit;">${label}</button>
  `;
  const tabs = `
    <div style="display:flex;gap:4px;border-bottom:1px solid ${C.border};margin-bottom:18px;">
      ${tabBtn("usuarios", "👥 Usuarios")}
      ${tabBtn("roles", "🎭 Roles y permisos")}
    </div>
  `;

  let body;
  if (tab === "roles") {
    body = renderRoles();
  } else if (state.usuariosCargando) {
    body = `<div style="padding:26px;text-align:center;color:${C.muted};background:white;border:1px solid ${C.border};border-radius:6px;">Cargando usuarios…</div>`;
  } else if (state.usuariosError) {
    body = `
      <div style="padding:30px;text-align:center;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;">
        <div style="font-size:32px;margin-bottom:8px;">⚠️</div>
        <div style="color:#dc2626;font-weight:700;font-size:12px;">${escapeHTML(state.usuariosError)}</div>
        <button data-action="usuarios-recargar" style="${btnStyle(null, "secondary")};margin-top:14px;">↺ Reintentar</button>
      </div>`;
  } else {
    body = renderTablaUsuarios(state.usuariosLista || []);
  }

  return header + tabs + body
    + (state.usuariosFormNuevo ? renderModalNuevoUsuario() : "")
    + (state.usuariosReset ? renderModalResetPassword() : "")
    + (state._rolEditor ? renderModalRol() : "")
    + (state._rolUsuarios ? renderModalUsuariosRol() : "");
}

// Contraseña temporal legible pero no adivinable: se genera con el generador
// criptográfico del navegador, no con Math.random. Se omiten los caracteres que
// se confunden al dictarla por teléfono (O/0, l/1, I).
//
// La política de contraseñas de Supabase Auth exige AL MENOS UN carácter de
// cada grupo (minúscula, mayúscula, dígito y símbolo): por eso se toma uno
// garantizado de cada uno y el resto del alfabeto completo, y luego se baraja —
// sin barajar, los cuatro primeros caracteres delatarían su grupo.
const GRUPOS_CLAVE = [
  "abcdefghijkmnopqrstuvwxyz",   // sin l
  "ABCDEFGHJKLMNPQRSTUVWXYZ",    // sin I ni O
  "23456789",                    // sin 0 ni 1
  "*+-=?!#$%@"                   // símbolos fáciles de dictar y aceptados por la política
];
const ALFABETO_CLAVE = GRUPOS_CLAVE.join("");

export function generarPasswordTemporal(largo = 14) {
  const bytes = new Uint32Array(largo);
  crypto.getRandomValues(bytes);
  const chars = [...bytes].map((b, i) => {
    const fuente = i < GRUPOS_CLAVE.length ? GRUPOS_CLAVE[i] : ALFABETO_CLAVE;
    return fuente[b % fuente.length];
  });
  // Barajado de Fisher-Yates, también con azar criptográfico.
  const azar = new Uint32Array(chars.length);
  crypto.getRandomValues(azar);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = azar[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}

// Política de caducidad: { habilitado, dias }. Vive en localStorage sincronizado
// a tenant_config (CONFIG_CLOUD_KEYS); el control de la puerta la lee de la nube.
export function getPoliticaPassword() {
  try {
    const raw = localStorage.getItem("erp:politica_password");
    const p = raw ? JSON.parse(raw) : {};
    return { habilitado: !!p.habilitado, dias: Number(p.dias) || 90 };
  } catch { return { habilitado: false, dias: 90 }; }
}
function savePoliticaPassword(p) {
  try { localStorage.setItem("erp:politica_password", JSON.stringify(p)); } catch {}
}

function seccionPoliticaPassword() {
  const p = getPoliticaPassword();
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:14px;">
      <h3 style="margin:0 0 4px;font-size:12px;color:${C.text};font-weight:700;">🗓️ CADUCIDAD DE CONTRASEÑAS</h3>
      <p style="margin:0 0 9px;font-size:10.5px;color:${C.muted};">
        Con la política activa, quien inicie sesión con una contraseña más vieja que el plazo
        no podrá entrar hasta definir una nueva. El reloj de cada usuario arranca en su último
        cambio de contraseña (o en su siguiente inicio de sesión, si nunca la ha cambiado).
      </p>
      <div style="display:flex;gap:14px;align-items:center;flex-wrap:wrap;">
        <label style="display:flex;align-items:center;gap:7px;font-size:11.5px;color:${C.text};cursor:pointer;">
          <input type="checkbox" data-action="politica-pass-toggle" ${p.habilitado ? "checked" : ""}/>
          <strong>Exigir cambio de contraseña cada</strong>
        </label>
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="number" min="7" max="365" value="${p.dias}" data-action="politica-pass-dias"
            ${p.habilitado ? "" : "disabled"}
            style="${inputStyle}width:80px;text-align:right;${p.habilitado ? "" : "opacity:.5;"}"/>
          <span style="font-size:11.5px;color:${C.muted};">días</span>
        </div>
        ${p.habilitado
          ? `<span style="font-size:10.5px;color:#059669;font-weight:700;">✅ Activa — se aplica en cada inicio de sesión</span>`
          : `<span style="font-size:10.5px;color:${C.muted};">Desactivada</span>`}
      </div>
    </div>
  `;
}

function seccionPoliticaSesion() {
  const p = getPoliticaSesion();
  return `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;padding:11px;margin-bottom:14px;">
      <h3 style="margin:0 0 4px;font-size:12px;color:${C.text};font-weight:700;">⏱️ CIERRE DE SESIÓN POR INACTIVIDAD</h3>
      <p style="margin:0 0 9px;font-size:10.5px;color:${C.muted};">
        Con la política activa, una sesión sin actividad (teclado, clics o movimiento del ratón)
        se cierra sola al cumplirse el plazo. Un minuto antes aparece un aviso con cuenta regresiva:
        cualquier actividad lo cancela. Aplica a todas las sesiones abiertas sin necesidad de recargar.
      </p>
      <div style="display:flex;gap:14px;align-items:center;flex-wrap:wrap;">
        <label style="display:flex;align-items:center;gap:7px;font-size:11.5px;color:${C.text};cursor:pointer;">
          <input type="checkbox" data-action="politica-sesion-toggle" ${p.habilitado ? "checked" : ""}/>
          <strong>Cerrar la sesión tras</strong>
        </label>
        <div style="display:flex;align-items:center;gap:6px;">
          <input type="number" min="5" max="480" value="${p.minutos}" data-action="politica-sesion-minutos"
            ${p.habilitado ? "" : "disabled"}
            style="${inputStyle}width:80px;text-align:right;${p.habilitado ? "" : "opacity:.5;"}"/>
          <span style="font-size:11.5px;color:${C.muted};">minutos sin actividad</span>
        </div>
        ${p.habilitado
          ? `<span style="font-size:10.5px;color:#059669;font-weight:700;">✅ Activa en todas las sesiones</span>`
          : `<span style="font-size:10.5px;color:${C.muted};">Desactivada</span>`}
      </div>
    </div>
  `;
}

function renderTablaUsuarios(usuarios) {
  if (usuarios.length === 0) {
    return seccionPoliticaPassword() + seccionPoliticaSesion() + `
      <div style="padding:26px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
        <div style="font-size:40px;margin-bottom:10px;">👥</div>
        <div style="color:${C.muted};font-size:14px;">Sin usuarios registrados aún.</div>
      </div>`;
  }

  const sel = state.usuariosSeleccion || {};
  // Seleccionables: activos y distintos de mí (forzarme a mí mismo no tiene sentido).
  const seleccionables = usuarios.filter(u => !u.desactivado && u.email !== state.usuarioEmail);
  const numSel = seleccionables.filter(u => sel[u.id]).length;

  const rows = usuarios.map(u => {
    const rolInfo = infoRol(u.rol);
    const esYo = u.email === state.usuarioEmail;
    const seleccionable = !u.desactivado && !esYo;
    const selectRol = esYo
      ? `<span style="font-size:11.5px;">${rolInfo.icon} ${escapeHTML(rolInfo.label)} <span style="color:${C.muted};font-size:10px;">(tú)</span></span>`
      : `<select data-action="usuarios-cambiar-rol" data-id="${u.id}" ${u.desactivado ? "disabled" : ""} style="${inputStyle}font-size:11.5px;padding:4px 8px;width:auto;cursor:pointer;">
          ${opcionesRolesHTML(u.rol)}
        </select>`;

    const estado = u.desactivado
      ? `<span style="color:#dc2626;font-weight:700;font-size:10.5px;">⛔ Desactivado</span>`
      : u.invitacion_pendiente
      ? `<span style="color:#d97706;font-weight:700;font-size:10.5px;">✉️ Invitación pendiente</span>`
      : `<span style="color:#059669;font-weight:700;font-size:10.5px;">✅ Activo</span>`;

    const actividad = `<button data-action="ver-actividad-usuario" data-id="${u.id}" data-email="${escapeHTML(u.email)}" data-nombre="${escapeHTML(u.nombre || u.email)}" style="${btnStyle(null, "ghost", true)}" title="Lo que hizo en AURA y lo que se le hizo a su cuenta">📜 Actividad</button>`;
    const acciones = esYo ? actividad : actividad + `
      ${u.desactivado ? "" : `<button data-action="usuarios-reset-password" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)}" title="Fijarle una contraseña nueva porque perdió la suya">♻️ Restablecer clave</button>`}
      ${u.desactivado ? "" : `<button data-action="usuarios-forzar-password" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)}" title="Obligar a que cambie su contraseña en el próximo inicio de sesión, conservando la actual hasta entonces">🔑 Forzar cambio de clave</button>`}
      ${u.desactivado ? "" : `<button data-action="usuarios-reiniciar-mfa" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)}" title="Borrar su vinculación con el autenticador (QR atascado o teléfono perdido): en su próximo inicio de sesión verá el código QR de nuevo">🔁 Reiniciar MFA</button>`}
      ${u.desactivado
        ? `<button data-action="usuarios-reactivar" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)}" title="Permitir que vuelva a iniciar sesión">✅ Reactivar</button>`
        : `<button data-action="usuarios-desactivar" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)}" title="Bloquear su inicio de sesión sin borrar sus datos">⛔ Desactivar</button>`}
      <button data-action="usuarios-eliminar" data-id="${u.id}" data-email="${escapeHTML(u.email)}" style="${btnStyle(null, "ghost", true)};color:#dc2626;" title="Eliminar definitivamente">🗑️</button>
    `;

    return `
      <tr style="border-top:1px solid ${C.border};${u.desactivado ? "opacity:.55;" : ""}">
        <td style="padding:6px 4px 6px 10px;text-align:center;">
          ${seleccionable
            ? `<input type="checkbox" data-action="usuarios-toggle-sel" data-id="${u.id}" ${sel[u.id] ? "checked" : ""} style="cursor:pointer;width:15px;height:15px;"/>`
            : ""}
        </td>
        <td style="padding:6px 10px;">
          <div style="font-weight:700;">${escapeHTML(u.nombre || "—")}</div>
          <div style="font-size:10.5px;color:${C.muted};">${escapeHTML(u.email || "—")}</div>
        </td>
        <td style="padding:6px 10px;">${selectRol}</td>
        <td style="padding:6px 10px;">${estado}</td>
        <td style="padding:6px 10px;text-align:center;">${u.mfa ? `<span title="Verificación en dos pasos activa">🔐</span>` : `<span style="color:${C.muted};font-size:10.5px;">—</span>`}</td>
        <td style="padding:6px 10px;font-size:10.5px;color:${C.muted};">${u.ultimo_acceso ? fmtDate(u.ultimo_acceso.slice(0, 10)) : "Nunca"}</td>
        <td style="padding:6px 10px;text-align:right;white-space:nowrap;">${acciones}</td>
      </tr>`;
  }).join("");

  const barraSeleccion = numSel > 0 ? `
    <div style="display:flex;gap:10px;align-items:center;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:8px 12px;margin-bottom:10px;flex-wrap:wrap;">
      <span style="font-size:11.5px;color:#92400e;font-weight:700;">${numSel} usuario${numSel !== 1 ? "s" : ""} seleccionado${numSel !== 1 ? "s" : ""}</span>
      <button data-action="usuarios-forzar-seleccionados" style="${btnStyle("#b45309")}">🔑 Forzar cambio de contraseña a los seleccionados</button>
      <button data-action="usuarios-limpiar-sel" style="${btnStyle(null, "secondary", true)}">Quitar selección</button>
      <span style="font-size:10px;color:${C.muted};">Si alguno tiene el panel abierto, verá un aviso con 3 minutos para guardar y su sesión se cerrará.</span>
    </div>` : "";

  return seccionPoliticaPassword() + seccionPoliticaSesion() + barraSeleccion + `
    <div style="background:white;border:1px solid ${C.border};border-radius:6px;overflow:auto;">
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead>
          <tr style="background:#f1f5f9;">
            <th style="padding:6px 4px 6px 10px;text-align:center;">
              <input type="checkbox" data-action="usuarios-toggle-sel-todos" ${seleccionables.length > 0 && numSel === seleccionables.length ? "checked" : ""} title="Seleccionar todos" style="cursor:pointer;width:15px;height:15px;"/>
            </th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">USUARIO</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">ROL</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">ESTADO</th>
            <th style="padding:6px 10px;text-align:center;font-size:10.5px;font-weight:700;color:${C.text};">MFA</th>
            <th style="padding:6px 10px;text-align:left;font-size:10.5px;font-weight:700;color:${C.text};">ÚLTIMO ACCESO</th>
            <th></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    </div>
    <div style="margin-top:10px;font-size:10.5px;color:${C.muted};">
      💡 "Desactivar" bloquea el inicio de sesión pero conserva todos los datos e historial del usuario — es reversible y casi siempre preferible a eliminar.
    </div>
  `;
}

// ─── Pestaña de roles ─────────────────────────────────────────────────────────

function totalPaginasNav() {
  return NAV_GRUPOS.reduce((s, g) => s + g.items.length, 0) + PAGINAS_EXTRA_ROLES.length;
}

function renderRoles() {
  const personalizados = getRolesPersonalizados();
  const usuarios = state.usuariosLista || [];
  const usuariosConRol = rolId => usuarios.filter(u => u.rol === rolId).length;

  const overrides = getRolesSistemaOverrides();
  const btnMiembros = rolId => {
    const n = usuariosConRol(rolId);
    return `<button data-action="rol-usuarios" data-id="${escapeHTML(rolId)}" title="Ver y administrar los usuarios con este rol"
      style="background:transparent;border:none;padding:0;font-size:10.5px;color:${C.accent};font-weight:700;cursor:pointer;text-decoration:underline;font-family:inherit;">👥 ${n} usuario${n !== 1 ? "s" : ""} — administrar</button>`;
  };

  const cardSistema = (rolId, info) => {
    const ajuste = overrides[rolId];
    const n = (ajuste && Array.isArray(ajuste.paginas) ? ajuste.paginas : (ROLE_PAGES[rolId] || [])).length;
    const paginas = rolId === "admin" ? "Acceso total al sistema" : `${n} página${n !== 1 ? "s" : ""}`;
    return `
      <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:14px 16px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
          <div style="font-size:14px;font-weight:800;color:${C.text};">${info.icon} ${info.label}</div>
          <div style="display:flex;gap:4px;align-items:center;">
            ${ajuste ? `<span title="Los permisos de fábrica fueron ajustados para tu empresa" style="font-size:9px;font-weight:800;letter-spacing:.5px;color:#92400e;background:#fde68a80;padding:2px 8px;border-radius:6px;">AJUSTADO</span>` : ""}
            <span style="font-size:9px;font-weight:800;letter-spacing:.5px;color:${C.muted};background:${C.border}60;padding:2px 8px;border-radius:6px;">SISTEMA</span>
            ${rolId === "admin" ? "" : `<button data-action="roles-editar-sistema" data-id="${escapeHTML(rolId)}" style="${btnStyle(null, "ghost", true)}" title="Ajustar los permisos de este rol para tu empresa">✏️</button>`}
          </div>
        </div>
        <div style="font-size:10.5px;color:${C.muted};margin-top:6px;">${paginas} · ${btnMiembros(rolId)}</div>
      </div>`;
  };

  const cardPersonalizado = rp => `
    <div style="background:white;border:1px solid ${NAVY}40;border-left:4px solid ${NAVY};border-radius:6px;padding:14px 16px;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:8px;">
        <div style="font-size:14px;font-weight:800;color:${NAVY};">🎭 ${escapeHTML(rp.nombre)}</div>
        <div style="display:flex;gap:4px;">
          <button data-action="roles-editar" data-id="${escapeHTML(rp.id)}" style="${btnStyle(null, "ghost", true)}" title="Editar permisos">✏️</button>
          <button data-action="roles-eliminar" data-id="${escapeHTML(rp.id)}" style="${btnStyle(null, "ghost", true)};color:#dc2626;" title="Eliminar rol">🗑️</button>
        </div>
      </div>
      <div style="font-size:10.5px;color:${C.muted};margin-top:6px;">
        ${(rp.paginas || []).length} de ${totalPaginasNav()} páginas · ${btnMiembros(rp.id)}
        ${rp.mfa_obligatorio ? ` · <span style="color:#92400e;font-weight:700;">🔐 MFA obligatorio</span>` : ""}
      </div>
    </div>`;

  return `
    <div style="margin-bottom:8px;font-size:11.5px;font-weight:800;letter-spacing:.6px;color:${C.muted};">ROLES DEL SISTEMA <span style="font-weight:500;">(sus permisos se pueden ajustar para tu empresa; el Administrador siempre tiene acceso total)</span></div>
    <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(230px,1fr));gap:10px;margin-bottom:22px;">
      ${Object.entries(ROLES_INFO).map(([r, info]) => cardSistema(r, info)).join("")}
    </div>
    <div style="margin-bottom:8px;font-size:11.5px;font-weight:800;letter-spacing:.6px;color:${NAVY};">ROLES PERSONALIZADOS</div>
    ${personalizados.length === 0 ? `
      <div style="padding:34px;text-align:center;background:white;border:2px dashed ${C.border};border-radius:6px;">
        <div style="font-size:36px;margin-bottom:8px;">🎭</div>
        <div style="color:${C.muted};font-size:12px;margin-bottom:9px;">Aún no has creado roles personalizados.<br>Crea uno y define con interruptores a qué módulos tiene acceso.</div>
        <button data-action="roles-nuevo" style="${btnStyle(NAVY)}">🎭 + Crear rol</button>
      </div>
    ` : `
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(260px,1fr));gap:10px;">
        ${personalizados.map(cardPersonalizado).join("")}
      </div>
    `}
    <div style="margin-top:14px;padding:9px;background:#f8fafc;border:1px solid #c7d2fe;border-radius:6px;font-size:11.5px;color:#3730a3;">
      💡 Los cambios en un rol aplican a todos los usuarios que lo tengan asignado, en su próxima acción o inicio de sesión. El Panel General siempre es visible para todos.
    </div>
  `;
}

// Interruptor visual (pill switch)
function switchHTML(on, action, dataAttrs) {
  return `
    <button data-action="${action}" ${dataAttrs} title="${on ? "Con acceso — clic para quitar" : "Sin acceso — clic para dar acceso"}"
      style="width:42px;height:23px;border-radius:6px;border:none;cursor:pointer;position:relative;flex-shrink:0;transition:background .15s;background:${on ? NAVY : "#cbd5e1"};padding:0;">
      <span style="position:absolute;top:2.5px;left:${on ? "21px" : "3px"};width:18px;height:18px;border-radius:50%;background:white;box-shadow:0 1px 3px rgba(0,0,0,.3);transition:left .15s;"></span>
    </button>`;
}

function gruposParaEditor() {
  return [...NAV_GRUPOS, { id: "otros", label: "Otros", icon: "🔔", items: PAGINAS_EXTRA_ROLES }];
}

function renderModalRol() {
  const ed = state._rolEditor;
  const paginas = new Set(ed.paginas || []);

  const filasPermisos = gruposParaEditor().map(g => {
    const todosOn = g.items.every(it => paginas.has(it.id));
    const algunoOn = g.items.some(it => paginas.has(it.id));
    const filaModulo = `
      <div style="display:flex;align-items:center;gap:10px;padding:6px 9px;background:${algunoOn ? NAVY + "0d" : "#f8fafc"};border-radius:6px;margin-top:10px;">
        <span style="font-size:14px;">${g.icon}</span>
        <span style="flex:1;font-size:11.5px;font-weight:800;letter-spacing:.5px;color:${C.text};">${g.label.toUpperCase()} <span style="font-weight:500;color:${C.muted};font-size:10px;">(módulo — controla todos sus submódulos)</span></span>
        ${switchHTML(todosOn, "rol-toggle-grupo", `data-grupo="${g.id}"`)}
      </div>`;
    const filasItems = g.items.map(it => `
      <div style="display:flex;align-items:center;gap:10px;padding:6px 12px 6px 34px;">
        <span style="font-size:11.5px;">${it.icon}</span>
        <span style="flex:1;font-size:11.5px;color:${C.text};">${it.label}</span>
        ${switchHTML(paginas.has(it.id), "rol-toggle-pagina", `data-pagina="${it.id}"`)}
      </div>`).join("");
    return filaModulo + filasItems;
  }).join("");

  return `
    <div data-action="roles-cerrar" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:640px;max-width:100%;max-height:92vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;background:linear-gradient(135deg,${NAVY}10,${C.gold}08);">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">${ed.esSistema ? "⚙️ Ajustar rol del sistema" : `🎭 ${ed.esNuevo ? "Crear rol" : "Editar rol"}`}</h3>
          <button data-action="roles-cerrar" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          ${ed.esSistema ? `
          <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;margin-bottom:10px;background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:8px 11px;">
            <div style="font-size:10.5px;color:${C.muted};line-height:1.5;">
              Estás ajustando <b style="color:${C.text};">${escapeHTML(ed.nombre)}</b> para tu empresa: el nombre no cambia,
              pero sus permisos sí. Sin ajustes, rigen los de fábrica.
            </div>
            <button data-action="rol-restaurar-sistema" style="${btnStyle(null, "secondary", true)}white-space:nowrap;" title="Volver a los permisos de fábrica de este rol">↺ Permisos de fábrica</button>
          </div>` : `
          <div style="display:grid;grid-template-columns:1fr auto;gap:10px;align-items:end;margin-bottom:6px;">
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Nombre del rol</label>
              <input data-action="rol-campo" data-campo="nombre" type="text" value="${escapeHTML(ed.nombre || "")}" placeholder="Ej: Auxiliar de facturación" style="${inputStyle}"/>
            </div>
            <div style="display:flex;align-items:center;gap:8px;padding-bottom:6px;">
              <span style="font-size:10.5px;font-weight:700;color:#92400e;">🔐 Exigir MFA</span>
              ${switchHTML(!!ed.mfa_obligatorio, "rol-toggle-mfa", "")}
            </div>
          </div>
          <div style="font-size:10px;color:${C.muted};margin-bottom:10px;">Si activas "Exigir MFA", los usuarios con este rol deberán configurar verificación en dos pasos en su próximo inicio de sesión.</div>`}

          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:14px;">
            <div style="font-size:11.5px;font-weight:800;letter-spacing:.5px;color:${NAVY};">PERMISOS POR MÓDULO Y SUBMÓDULO</div>
            <div style="display:flex;gap:6px;">
              <button data-action="rol-todo" data-valor="1" style="${btnStyle(null, "secondary", true)}">Activar todo</button>
              <button data-action="rol-todo" data-valor="0" style="${btnStyle(null, "secondary", true)}">Desactivar todo</button>
            </div>
          </div>
          <div style="border:1px solid ${C.border};border-radius:6px;padding:4px 8px 10px;margin-top:8px;">
            <div style="display:flex;align-items:center;gap:10px;padding:6px 9px;background:#f8fafc;border-radius:6px;margin-top:6px;">
              <span style="font-size:14px;">📊</span>
              <span style="flex:1;font-size:11.5px;font-weight:700;color:${C.text};">Panel General (dashboard)</span>
              <span style="font-size:10px;font-weight:700;color:#059669;">SIEMPRE VISIBLE</span>
            </div>
            ${filasPermisos}
          </div>

          ${ed.error ? `<div style="margin-top:14px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:6px 10px;font-size:11.5px;color:#dc2626;font-weight:700;">⚠️ ${escapeHTML(ed.error)}</div>` : ""}

          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:18px;padding-top:14px;border-top:1px solid ${C.border};">
            <div style="font-size:10.5px;color:${C.muted};">${(ed.paginas || []).length} de ${totalPaginasNav()} páginas con acceso</div>
            <div style="display:flex;gap:10px;">
              <button data-action="roles-cerrar" style="${btnStyle(null, "secondary")}">Cancelar</button>
              <button data-action="roles-guardar" style="${btnStyle(NAVY)}">💾 Guardar rol</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderModalUsuariosRol() {
  const rolId = state._rolUsuarios.rolId;
  const info = infoRol(rolId);
  const usuarios = state.usuariosLista || [];
  const miembros = usuarios.filter(u => u.rol === rolId);
  const candidatos = usuarios.filter(u => u.rol !== rolId && u.email !== state.usuarioEmail && !u.desactivado);

  const filas = miembros.map(u => {
    const esYo = u.email === state.usuarioEmail;
    return `
      <div style="display:flex;align-items:center;gap:10px;padding:8px 0;border-top:1px solid ${C.border};">
        <div style="flex:1;min-width:0;">
          <div style="font-size:12px;font-weight:700;color:${C.text};">${escapeHTML(u.nombre || "—")} ${esYo ? `<span style="color:${C.muted};font-size:10px;font-weight:600;">(tú)</span>` : ""}</div>
          <div style="font-size:10.5px;color:${C.muted};">${escapeHTML(u.email || "—")}${u.desactivado ? ` · <span style="color:#dc2626;font-weight:700;">⛔ desactivado</span>` : ""}</div>
        </div>
        ${esYo
          ? `<span style="font-size:10px;color:${C.muted};">Tu propio rol se cambia desde otro administrador.</span>`
          : `<select data-action="usuarios-cambiar-rol" data-id="${u.id}" title="Moverlo a otro rol"
              style="${inputStyle}font-size:11px;padding:4px 8px;width:auto;cursor:pointer;">
              ${opcionesRolesHTML(u.rol)}
            </select>`}
      </div>`;
  }).join("");

  return `
    <div data-action="rol-usuarios-cerrar" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:520px;max-width:100%;max-height:88vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">${info.icon} Usuarios con el rol "${escapeHTML(info.label)}"</h3>
          <button data-action="rol-usuarios-cerrar" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          ${state.usuariosCargando
            ? `<div style="padding:18px;text-align:center;color:${C.muted};font-size:12px;">Cargando usuarios…</div>`
            : miembros.length === 0
            ? `<div style="padding:18px;text-align:center;color:${C.muted};font-size:12px;background:#f8fafc;border-radius:6px;">Ningún usuario tiene este rol todavía.</div>`
            : `<div style="font-size:10px;color:${C.muted};margin-bottom:2px;">Para mover a alguien a otro rol, elígelo en su lista. El cambio aplica de inmediato.</div>${filas}`}

          ${candidatos.length ? `
          <div style="margin-top:14px;padding-top:12px;border-top:1px solid ${C.border};">
            <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Asignar este rol a otro usuario</label>
            <div style="display:flex;gap:6px;">
              <select id="rol-usuarios-candidato" style="${inputStyle}flex:1;cursor:pointer;">
                ${candidatos.map(u => `<option value="${u.id}">${escapeHTML(u.nombre || u.email)} — ${escapeHTML(infoRol(u.rol).label)}</option>`).join("")}
              </select>
              <button data-action="rol-usuarios-agregar" data-rol="${escapeHTML(rolId)}" style="${btnStyle(NAVY)}white-space:nowrap;">➕ Asignar</button>
            </div>
          </div>` : ""}

          <div style="display:flex;justify-content:flex-end;margin-top:14px;padding-top:12px;border-top:1px solid ${C.border};">
            <button data-action="rol-usuarios-cerrar" style="${btnStyle(null, "secondary")}">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderModalNuevoUsuario() {
  const f = state.usuariosFormNuevo;
  const exigeMFA = rolExigeMFA(f.rol);
  const esInvitacion = f.modo === "invitar";

  return `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:480px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">👥 Nuevo usuario</h3>
          <button data-action="usuarios-cerrar-form" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">

          <div style="display:flex;gap:8px;margin-bottom:10px;">
            <button data-action="usuarios-form-modo" data-modo="crear" style="flex:1;padding:9px;border-radius:6px;border:2px solid ${!esInvitacion ? "#6b7280" : C.border};background:${!esInvitacion ? "#6b728015" : "white"};font-weight:700;font-size:11.5px;cursor:pointer;font-family:inherit;">🔑 Crear con contraseña</button>
            <button data-action="usuarios-form-modo" data-modo="invitar" style="flex:1;padding:9px;border-radius:6px;border:2px solid ${esInvitacion ? "#6b7280" : C.border};background:${esInvitacion ? "#6b728015" : "white"};font-weight:700;font-size:11.5px;cursor:pointer;font-family:inherit;">✉️ Invitar por correo</button>
          </div>

          <div style="display:grid;gap:9px;">
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Nombre completo</label>
              <input data-action="usuarios-form-campo" data-campo="nombre" type="text" value="${escapeHTML(f.nombre || "")}" style="${inputStyle}"/>
            </div>
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Correo electrónico</label>
              <input data-action="usuarios-form-campo" data-campo="email" type="email" value="${escapeHTML(f.email || "")}" style="${inputStyle}"/>
            </div>
            ${!esInvitacion ? `
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Contraseña inicial — mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo</label>
              <div style="display:flex;gap:6px;">
                <input data-action="usuarios-form-campo" data-campo="password" type="text" value="${escapeHTML(f.password || "")}" autocomplete="off" spellcheck="false"
                  style="${inputStyle}font-family:ui-monospace,Menlo,Consolas,monospace;letter-spacing:.5px;"/>
                <button data-action="usuarios-form-generar" style="${btnStyle(null, "secondary")}white-space:nowrap;" title="Generar una contraseña temporal segura">🎲 Generar</button>
              </div>
              <div style="font-size:10px;color:${C.muted};margin-top:3px;">Tú se la comunicas al usuario. Cópiala antes de guardar: no se vuelve a mostrar.</div>
            </div>
            <label style="display:flex;align-items:flex-start;gap:8px;background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:8px 10px;cursor:pointer;">
              <input type="checkbox" data-action="usuarios-form-toggle-forzar" ${f.forzarCambio !== false ? "checked" : ""} style="margin-top:2px;"/>
              <span style="font-size:11.5px;color:${C.text};">
                <strong>Debe cambiar la contraseña en su primer inicio de sesión</strong>
                <div style="font-size:10px;color:${C.muted};margin-top:2px;">
                  Recomendado: la contraseña inicial la conoces tú, así que solo debe servir para entrar una vez.
                </div>
              </span>
            </label>` : `
            <div style="background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:6px 10px;font-size:10.5px;color:${C.muted};">
              ✉️ El usuario recibirá un correo con un enlace para fijar su propia contraseña. El correo integrado de Supabase tiene un límite de pocas invitaciones por hora — para altas masivas usa "Crear con contraseña".
            </div>`}
            <div>
              <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Rol</label>
              <select data-action="usuarios-form-campo" data-campo="rol" style="${inputStyle}cursor:pointer;">
                ${opcionesRolesHTML(f.rol)}
              </select>
            </div>
          </div>

          ${exigeMFA ? `
          <div style="margin-top:14px;background:#fffbeb;border:1px solid #fde68a;border-radius:6px;padding:6px 10px;font-size:10.5px;color:#92400e;">
            🔐 Este rol tiene acceso a información sensible: en su primer inicio de sesión se le exigirá activar la verificación en dos pasos antes de poder entrar.
          </div>` : ""}

          ${f.error ? `<div style="margin-top:14px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:6px 10px;font-size:11.5px;color:#dc2626;font-weight:700;">⚠️ ${escapeHTML(f.error)}</div>` : ""}

          <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:20px;padding-top:16px;border-top:1px solid ${C.border};">
            <button data-action="usuarios-cerrar-form" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="usuarios-guardar-nuevo" style="${btnStyle("#6b7280")}" ${f.guardando ? "disabled" : ""}>${f.guardando ? "Guardando…" : (esInvitacion ? "✉️ Enviar invitación" : "💾 Crear usuario")}</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function renderModalResetPassword() {
  const r = state.usuariosReset;
  const corta = (r.password || "").length > 0 && (r.password || "").length < 8;

  return `
    <div data-action="close-modal-bg" style="position:fixed;top:0;right:0;bottom:0;left:0;background:rgba(15,23,42,.45);display:flex;align-items:center;justify-content:center;z-index:1000;padding:11px;">
      <div data-action="prevent-close" style="background:white;border-radius:14px;width:460px;max-width:100%;max-height:90vh;overflow:auto;box-shadow:0 20px 60px rgba(0,0,0,.2);">
        <div style="padding:10px 12px;border-bottom:1px solid ${C.border};display:flex;justify-content:space-between;align-items:center;">
          <h3 style="margin:0;font-size:16px;color:${C.text};font-weight:700;">♻️ Restablecer contraseña</h3>
          <button data-action="usuarios-reset-cerrar" style="background:transparent;border:none;font-size:16px;color:${C.muted};cursor:pointer;">✕</button>
        </div>
        <div style="padding:11px;">
          <div style="font-size:11.5px;color:${C.muted};margin-bottom:11px;">
            Usuario: <b style="color:${C.text};">${escapeHTML(r.email)}</b>
          </div>

          <label style="font-size:10.5px;color:${C.muted};font-weight:600;display:block;margin-bottom:4px;">Contraseña nueva — mínimo 8 caracteres, con mayúscula, minúscula, número y símbolo</label>
          <div style="display:flex;gap:6px;">
            <input id="usuarios-reset-input" data-action="usuarios-reset-campo" data-campo="password" type="text"
              value="${escapeHTML(r.password || "")}" autocomplete="off" spellcheck="false"
              style="${inputStyle}font-family:ui-monospace,Menlo,Consolas,monospace;letter-spacing:.5px;"/>
            <button data-action="usuarios-reset-generar" style="${btnStyle(null, "secondary")}white-space:nowrap;" title="Generar una contraseña temporal segura">🎲 Generar</button>
          </div>
          ${corta ? `<div style="font-size:10px;color:${C.red};margin-top:3px;font-weight:700;">Aún es demasiado corta.</div>` : ""}
          <div style="font-size:10px;color:${C.muted};margin-top:3px;">
            Cópiala antes de guardar: no se vuelve a mostrar. Comunícasela por un canal aparte, no por el mismo medio donde llegó el aviso.
          </div>

          <label style="display:flex;align-items:flex-start;gap:8px;margin-top:14px;background:#f8fafc;border:1px solid ${C.border};border-radius:6px;padding:8px 10px;cursor:pointer;">
            <input type="checkbox" data-action="usuarios-reset-toggle-forzar" ${r.forzarCambio ? "checked" : ""} style="margin-top:2px;"/>
            <span style="font-size:11.5px;color:${C.text};">
              <strong>Debe cambiarla en su primer inicio de sesión</strong>
              <div style="font-size:10px;color:${C.muted};margin-top:2px;">
                Recomendado: esta contraseña la conoces tú, así que solo debe servir para entrar una vez.
                Al desmarcarlo, la contraseña queda como definitiva y sabida por dos personas.
              </div>
            </span>
          </label>

          ${r.error ? `<div style="margin-top:14px;background:#fef2f2;border:1px solid #fca5a5;border-radius:6px;padding:6px 10px;font-size:11.5px;color:${C.red};font-weight:700;">⚠️ ${escapeHTML(r.error)}</div>` : ""}

          <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:20px;padding-top:16px;border-top:1px solid ${C.border};">
            <button data-action="usuarios-reset-cerrar" style="${btnStyle(null, "secondary")}">Cancelar</button>
            <button data-action="usuarios-reset-guardar" style="${btnStyle("#b45309")}" ${r.guardando ? "disabled" : ""}>
              ${r.guardando ? "Guardando…" : "♻️ Restablecer"}
            </button>
          </div>
        </div>
      </div>
    </div>
  `;
}

// ─── Acciones ────────────────────────────────────────────────────────────────

import { registerActions } from "../ui/actions.js";

registerActions({
  "usuarios-recargar": () => {
    recargarUsuarios();
  },
  "usuarios-tab": (d) => {
    state.usuariosTab = d.tab;
    render();
  },
  "usuarios-nuevo": () => {
    state.usuariosFormNuevo = {
      modo: "crear", nombre: "", email: "",
      password: generarPasswordTemporal(), rol: "operaciones",
      forzarCambio: true
    };
    render();
  },
  "usuarios-form-generar": () => {
    const f = state.usuariosFormNuevo;
    if (!f) return;
    f.password = generarPasswordTemporal();
    f.error = null;
    render();
  },
  "usuarios-form-toggle-forzar": () => {
    const f = state.usuariosFormNuevo;
    if (!f) return;
    f.forzarCambio = f.forzarCambio === false;
    render();
  },
  "usuarios-cerrar-form": () => {
    state.usuariosFormNuevo = null;
    render();
  },
  "usuarios-form-modo": (d) => {
    state.usuariosFormNuevo.modo = d.modo;
    state.usuariosFormNuevo.error = null;
    render();
  },
  "usuarios-form-campo": (d, el) => {
    state.usuariosFormNuevo[d.campo] = el.value;
    if (d.campo === "rol") render(); // refresca el aviso de MFA obligatorio
  },
  "usuarios-guardar-nuevo": async () => {
    const f = state.usuariosFormNuevo;
    if (!f || f.guardando) return;
    f.guardando = true;
    f.error = null;
    render();
    try {
      if (f.modo === "invitar") {
        await llamarAdminUsuarios({ accion: "invitar", email: f.email, nombre: f.nombre, rol: f.rol, redirectTo: location.origin });
        alert(`✅ Invitación enviada a ${f.email}. El usuario debe abrir el correo y fijar su contraseña.`);
      } else {
        const forzarCambio = f.forzarCambio !== false;
        const creado = await llamarAdminUsuarios({
          accion: "crear", email: f.email, password: f.password,
          nombre: f.nombre, rol: f.rol, forzarCambio
        });
        // La marca se pide DOS veces a propósito: "crear" ya la aplica de forma
        // atómica, pero las Edge Functions se despliegan aparte del frontend, así
        // que una versión anterior de admin-usuarios ignoraría "forzarCambio" en
        // silencio y el aviso de abajo prometería algo falso. Repetirla con una
        // acción que existe desde antes lo garantiza en ambos casos. Si falla, el
        // usuario ya está creado: se avisa en vez de callarlo.
        let marcaOk = true;
        if (forzarCambio && creado && creado.id) {
          try {
            await llamarAdminUsuarios({ accion: "forzar_cambio_password", userId: creado.id });
          } catch { marcaOk = false; }
        }
        // Se repite la contraseña aquí porque es el último momento en que se puede
        // copiar: el ERP no la guarda en ninguna parte.
        alert(`✅ Usuario ${f.email} creado.\n\nContraseña inicial:\n\n${f.password}\n\nComunícasela por un canal seguro.`
          + (forzarCambio
            ? (marcaOk
              ? " Deberá cambiarla al iniciar sesión."
              : "\n\n⚠️ No se pudo activar la exigencia de cambio de contraseña. Usa \"🔑 Forzar cambio de clave\" en la lista para aplicarla.")
            : ""));
      }
      state.usuariosFormNuevo = null;
      await recargarUsuarios();
    } catch (e) {
      f.guardando = false;
      f.error = e.message;
      render();
    }
  },
  "usuarios-cambiar-rol": async (d, el) => {
    const nuevoRol = el.value;
    const u = (state.usuariosLista || []).find(x => x.id === d.id);
    if (!u || u.rol === nuevoRol) return;
    if (!confirm(`¿Cambiar el rol de ${u.email} a "${infoRol(nuevoRol).label}"?`)) {
      render(); // restaura el select al valor original
      return;
    }
    try {
      await llamarAdminUsuarios({ accion: "cambiar_rol", userId: d.id, rol: nuevoRol });
      await recargarUsuarios();
    } catch (e) {
      alert("⚠️ " + e.message);
      render();
    }
  },
  "usuarios-desactivar": async (d) => {
    if (!confirm(`¿Desactivar a ${d.email}? No podrá iniciar sesión hasta que lo reactives; sus datos e historial se conservan.`)) return;
    try {
      await llamarAdminUsuarios({ accion: "desactivar", userId: d.id });
      await recargarUsuarios();
    } catch (e) { alert("⚠️ " + e.message); }
  },
  "usuarios-reactivar": async (d) => {
    try {
      await llamarAdminUsuarios({ accion: "reactivar", userId: d.id });
      await recargarUsuarios();
    } catch (e) { alert("⚠️ " + e.message); }
  },
  // ── Política de caducidad de contraseñas ────────────────────────────────
  "politica-pass-toggle": (d, el) => {
    const p = getPoliticaPassword();
    p.habilitado = el.checked;
    savePoliticaPassword(p);
    render();
  },
  "politica-pass-dias": (d, el, e) => {
    // Solo al confirmar (change): guardar en cada tecla haría que "30" pasara
    // un instante por "3 días" en la nube, y un login en ese segundo echaría a
    // alguien por una política que nadie configuró.
    if (e && e.type === "input") return;
    const p = getPoliticaPassword();
    const dias = parseInt(el.value, 10);
    // Menos de 7 días es hostigar a la gente sin ganancia de seguridad real.
    p.dias = isNaN(dias) ? 90 : Math.min(365, Math.max(7, dias));
    savePoliticaPassword(p);
    if (String(p.dias) !== el.value) el.value = p.dias;
  },

  // ── Política de cierre por inactividad ──────────────────────────────────
  "politica-sesion-toggle": (d, el) => {
    const p = getPoliticaSesion();
    p.habilitado = el.checked;
    try { localStorage.setItem("erp:politica_sesion", JSON.stringify(p)); } catch { /* sin caché local */ }
    render();
  },
  "politica-sesion-minutos": (d, el, e) => {
    // Solo al confirmar (change): guardar en cada tecla haría que "30" pasara
    // un instante por "3 minutos" y las sesiones abiertas obedecen en vivo.
    if (e && e.type === "input") return;
    const p = getPoliticaSesion();
    const min = parseInt(el.value, 10);
    p.minutos = isNaN(min) ? 15 : Math.min(480, Math.max(5, min));
    try { localStorage.setItem("erp:politica_sesion", JSON.stringify(p)); } catch { /* sin caché local */ }
    if (String(p.minutos) !== el.value) el.value = p.minutos;
  },

  // ── Selección múltiple para forzar cambio de contraseña ─────────────────
  "usuarios-toggle-sel": (d) => {
    if (!state.usuariosSeleccion) state.usuariosSeleccion = {};
    if (state.usuariosSeleccion[d.id]) delete state.usuariosSeleccion[d.id];
    else state.usuariosSeleccion[d.id] = true;
    render();
  },
  "usuarios-toggle-sel-todos": (d, el) => {
    const seleccionables = (state.usuariosLista || [])
      .filter(u => !u.desactivado && u.email !== state.usuarioEmail);
    state.usuariosSeleccion = {};
    if (el.checked) seleccionables.forEach(u => { state.usuariosSeleccion[u.id] = true; });
    render();
  },
  "usuarios-limpiar-sel": () => {
    state.usuariosSeleccion = {};
    render();
  },
  "usuarios-forzar-seleccionados": async () => {
    const sel = state.usuariosSeleccion || {};
    const objetivos = (state.usuariosLista || [])
      .filter(u => sel[u.id] && !u.desactivado && u.email !== state.usuarioEmail);
    if (!objetivos.length) return;
    const lista = objetivos.map(u => "  • " + u.email).join("\n");
    if (!confirm(`¿Forzar el cambio de contraseña a ${objetivos.length} usuario${objetivos.length !== 1 ? "s" : ""}?\n\n${lista}\n\nQuien tenga el panel abierto verá un aviso con 3 minutos para guardar su trabajo y su sesión se cerrará. Los demás quedarán detenidos en su próximo inicio de sesión.`)) return;

    // Uno por uno a propósito: si alguno falla, los demás no se pierden y el
    // resumen dice exactamente quién quedó pendiente.
    const fallos = [];
    for (const u of objetivos) {
      try {
        await llamarAdminUsuarios({ accion: "forzar_cambio_password", userId: u.id });
      } catch (e) {
        fallos.push(`${u.email}: ${e.message}`);
      }
    }
    state.usuariosSeleccion = {};
    if (fallos.length) {
      alert(`⚠️ Se aplicó a ${objetivos.length - fallos.length} de ${objetivos.length}. Fallaron:\n\n${fallos.join("\n")}`);
    } else {
      alert(`✅ Listo. ${objetivos.length} usuario${objetivos.length !== 1 ? "s" : ""} deberá${objetivos.length !== 1 ? "n" : ""} cambiar su contraseña. Las sesiones abiertas recibirán el aviso de 3 minutos.`);
    }
    await recargarUsuarios();
  },

  // ── Restablecer contraseña ──────────────────────────────────────────────
  "usuarios-reset-password": (d) => {
    state.usuariosReset = {
      id: d.id, email: d.email,
      password: generarPasswordTemporal(),   // ya viene propuesta, se puede sobrescribir
      forzarCambio: true,
      guardando: false, error: null
    };
    render();
  },
  "usuarios-reset-cerrar": () => {
    state.usuariosReset = null;
    render();
  },
  "usuarios-reset-campo": (d, el) => {
    if (state.usuariosReset) state.usuariosReset[d.campo] = el.value;
  },
  "usuarios-reset-generar": () => {
    const r = state.usuariosReset;
    if (!r) return;
    r.password = generarPasswordTemporal();
    r.error = null;
    render();
  },
  "usuarios-reset-toggle-forzar": () => {
    const r = state.usuariosReset;
    if (!r) return;
    r.forzarCambio = !r.forzarCambio;
    render();
  },
  "usuarios-reset-guardar": async () => {
    const r = state.usuariosReset;
    if (!r || r.guardando) return;
    if ((r.password || "").length < 8) {
      r.error = "La contraseña debe tener al menos 8 caracteres.";
      render();
      return;
    }
    if (!confirm(`¿Restablecer la contraseña de ${r.email}?\n\nSu contraseña actual deja de funcionar de inmediato.${r.forzarCambio ? "\nDeberá cambiarla al iniciar sesión." : ""}`)) return;
    r.guardando = true;
    r.error = null;
    render();
    try {
      await llamarAdminUsuarios({
        accion: "restablecer_password", userId: r.id,
        password: r.password, forzarCambio: r.forzarCambio
      });
      // La contraseña se repite en el aviso porque es el último momento en que se
      // puede copiar: no queda guardada en ninguna parte del ERP.
      alert(`✅ Contraseña restablecida para ${r.email}.\n\nContraseña temporal:\n\n${r.password}\n\nComunícasela por un canal seguro.${r.forzarCambio ? " Deberá cambiarla al iniciar sesión." : ""}`);
      state.usuariosReset = null;
      await recargarUsuarios();
    } catch (e) {
      r.guardando = false;
      r.error = e.message;
      render();
    }
  },
  "usuarios-reiniciar-mfa": async (d) => {
    if (!confirm(`¿Reiniciar la verificación en dos pasos de ${d.email}?\n\nSe borra su vinculación actual con el autenticador (incluidos los intentos a medias). En su próximo inicio de sesión verá el código QR para vincular Microsoft Authenticator de nuevo.\n\nÚsalo cuando el QR no aparece o cuando perdió el teléfono.`)) return;
    try {
      const r = await llamarAdminUsuarios({ accion: "reiniciar_mfa", userId: d.id });
      alert(`✅ Verificación en dos pasos reiniciada para ${d.email} (${r.eliminados || 0} factor${(r.eliminados || 0) !== 1 ? "es" : ""} eliminado${(r.eliminados || 0) !== 1 ? "s" : ""}).\n\nEn su próximo inicio de sesión se le pedirá vincular el autenticador con un QR nuevo.`);
      await recargarUsuarios();
    } catch (e) { alert("⚠️ " + e.message); }
  },
  "usuarios-forzar-password": async (d) => {
    if (!confirm(`¿Obligar a ${d.email} a cambiar su contraseña?\n\nEn su próximo inicio de sesión no podrá entrar hasta definir una contraseña nueva. Su contraseña actual sigue siendo válida hasta ese momento.`)) return;
    try {
      await llamarAdminUsuarios({ accion: "forzar_cambio_password", userId: d.id });
      alert(`✅ Listo. ${d.email} deberá cambiar su contraseña en el próximo inicio de sesión.`);
    } catch (e) { alert("⚠️ " + e.message); }
  },
  "usuarios-eliminar": async (d) => {
    if (!confirm(`⚠️ ¿Eliminar DEFINITIVAMENTE a ${d.email}?\n\nEsta acción no se puede deshacer. Si solo quieres bloquear su acceso, usa "Desactivar".`)) return;
    const confirmacion = prompt(`Para confirmar, escribe el correo del usuario:\n${d.email}`);
    if (confirmacion !== d.email) {
      if (confirmacion !== null) alert("El correo no coincide. No se eliminó nada.");
      return;
    }
    try {
      await llamarAdminUsuarios({ accion: "eliminar", userId: d.id });
      await recargarUsuarios();
    } catch (e) { alert("⚠️ " + e.message); }
  },

  // ── Roles personalizados ────────────────────────────────────────────────
  "roles-nuevo": () => {
    state.usuariosTab = "roles";
    state._rolEditor = { esNuevo: true, id: null, nombre: "", mfa_obligatorio: false, paginas: [] };
    render();
  },
  "roles-editar": (d) => {
    const rp = getRolesPersonalizados().find(r => r.id === d.id);
    if (!rp) return;
    state._rolEditor = { esNuevo: false, id: rp.id, nombre: rp.nombre, mfa_obligatorio: !!rp.mfa_obligatorio, paginas: [...(rp.paginas || [])] };
    render();
  },
  "roles-cerrar": () => {
    state._rolEditor = null;
    render();
  },
  "rol-campo": (d, el) => {
    if (state._rolEditor) state._rolEditor[d.campo] = el.value;
  },
  "rol-toggle-mfa": () => {
    const ed = state._rolEditor;
    if (!ed) return;
    ed.mfa_obligatorio = !ed.mfa_obligatorio;
    render();
  },
  "rol-toggle-pagina": (d) => {
    const ed = state._rolEditor;
    if (!ed) return;
    const set = new Set(ed.paginas || []);
    if (set.has(d.pagina)) set.delete(d.pagina); else set.add(d.pagina);
    ed.paginas = [...set];
    render();
  },
  // Interruptor del módulo: los submódulos heredan su estado (enciende o
  // apaga todos los submódulos del grupo de una sola vez).
  "rol-toggle-grupo": (d) => {
    const ed = state._rolEditor;
    if (!ed) return;
    const grupo = gruposParaEditor().find(g => g.id === d.grupo);
    if (!grupo) return;
    const set = new Set(ed.paginas || []);
    const todosOn = grupo.items.every(it => set.has(it.id));
    grupo.items.forEach(it => { if (todosOn) set.delete(it.id); else set.add(it.id); });
    ed.paginas = [...set];
    render();
  },
  "rol-todo": (d) => {
    const ed = state._rolEditor;
    if (!ed) return;
    ed.paginas = d.valor === "1" ? gruposParaEditor().flatMap(g => g.items.map(it => it.id)) : [];
    render();
  },
  "roles-guardar": () => {
    const ed = state._rolEditor;
    if (!ed) return;
    if (ed.esSistema) {
      if ((ed.paginas || []).length === 0) { ed.error = "Activa al menos una página — un rol sin acceso a nada no puede trabajar."; render(); return; }
      const overrides = getRolesSistemaOverrides();
      const fabrica = [...(ROLE_PAGES[ed.id] || [])].sort().join(",");
      const elegidas = [...ed.paginas].sort().join(",");
      // Igual que fábrica → sin ajuste: así la tarjeta no marca "AJUSTADO" en falso
      // y una futura mejora de los permisos de fábrica aplica sola.
      if (elegidas === fabrica) delete overrides[ed.id];
      else overrides[ed.id] = { paginas: [...ed.paginas] };
      saveRolesSistemaOverrides(overrides);
      state._rolEditor = null;
      render();
      return;
    }
    const nombre = (ed.nombre || "").trim();
    if (!nombre) { ed.error = "El nombre del rol es obligatorio."; render(); return; }
    const lista = getRolesPersonalizados();
    const nombreOcupado = lista.some(r => r.nombre.toLowerCase() === nombre.toLowerCase() && r.id !== ed.id)
      || Object.values(ROLES_INFO).some(info => info.label.toLowerCase() === nombre.toLowerCase());
    if (nombreOcupado) { ed.error = "Ya existe un rol con ese nombre."; render(); return; }
    if ((ed.paginas || []).length === 0) { ed.error = "Activa al menos una página — un rol sin acceso a nada no puede trabajar."; render(); return; }

    const rol = { id: ed.id || "rp_" + genId(), nombre, paginas: [...ed.paginas], mfa_obligatorio: !!ed.mfa_obligatorio };
    const idx = lista.findIndex(r => r.id === rol.id);
    if (idx >= 0) lista[idx] = rol; else lista.push(rol);
    saveRolesPersonalizados(lista);
    state._rolEditor = null;
    render();
  },
  "roles-editar-sistema": (d) => {
    if (d.id === "admin" || !ROLES_INFO[d.id]) return;
    state.usuariosTab = "roles";
    const ajuste = getRolesSistemaOverrides()[d.id];
    state._rolEditor = {
      esSistema: true, esNuevo: false, id: d.id,
      nombre: ROLES_INFO[d.id].label,
      mfa_obligatorio: false,   // el MFA es obligatorio para todos; no aplica aquí
      paginas: [...(ajuste && Array.isArray(ajuste.paginas) ? ajuste.paginas : (ROLE_PAGES[d.id] || []))]
    };
    render();
  },
  "rol-restaurar-sistema": () => {
    const ed = state._rolEditor;
    if (!ed || !ed.esSistema) return;
    ed.paginas = [...(ROLE_PAGES[ed.id] || [])];
    ed.error = null;
    render();
  },
  "rol-usuarios": (d) => {
    state._rolUsuarios = { rolId: d.id };
    render();
  },
  "rol-usuarios-cerrar": () => {
    state._rolUsuarios = null;
    render();
  },
  "rol-usuarios-agregar": async (d) => {
    const sel = document.getElementById("rol-usuarios-candidato");
    if (!sel || !sel.value) return;
    try {
      await llamarAdminUsuarios({ accion: "cambiar_rol", userId: sel.value, rol: d.rol });
      await recargarUsuarios();   // el modal sigue abierto: la lista se refresca sola
    } catch (e) { alert("⚠️ " + e.message); }
  },
  "roles-eliminar": (d) => {
    const rp = getRolesPersonalizados().find(r => r.id === d.id);
    if (!rp) return;
    const enUso = (state.usuariosLista || []).filter(u => u.rol === d.id);
    if (enUso.length > 0) {
      alert(`⚠️ No se puede eliminar el rol "${rp.nombre}": está asignado a ${enUso.length} usuario${enUso.length !== 1 ? "s" : ""} (${enUso.map(u => u.email).join(", ")}).\n\nCambia primero el rol de esos usuarios.`);
      return;
    }
    if (!confirm(`¿Eliminar el rol "${rp.nombre}"?`)) return;
    saveRolesPersonalizados(getRolesPersonalizados().filter(r => r.id !== d.id));
    render();
  },
});
